import EditIcon from '@mui/icons-material/Edit';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  DialogContentText,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { splitProductData, updateProductFormData, openProductFile } from '../../../../../api/catalog';
import { getProductDetails } from '../../../../../redux/slices/catalog';
import { useDispatch, useSelector } from '../../../../../redux/store';
import { AwaitButton } from '../../../../../reusable-components/await-button';
import { ConfirmDialog } from '../../../../../reusable-components/confirm-dialog';
import CustomDataGrid from '../../../../../reusable-components/datagrid/CustomDataGrid';
import { CATALOG_ITEM } from '../../../../../reusable-components/datagrid/catalogColumns';
import Iconify from '../../../../../reusable-components/iconify';
import { ScopeGuard } from '../../../../../reusable-components/scopes';
import { useSnackbar } from '../../../../../reusable-components/snackbar';
import ItemAISuggestion from '../../../../../reusable-components/itemAISuggestion/itemsuggest';
import { addHyperlink } from '../../../../../utils/formatText';
import NewItemDialog from '../NewItemDialog';
import OrderGuideDialog from '../OrderGuideDialog';
import EditItemDialog from './EditItemDialog';
import { updateOrderItemFullInRedux } from '../../../../../redux/slices/orders';
import FileAttachment from '../../../../../reusable-components/file-attachment/FileAttachment';
import ProductFileAttachmentButton from '../ProductFileAttachmentButton';

CatalogItemView.propTypes = {
  row: PropTypes.object,
};

export default function CatalogItemView({ row }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id = '' } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [fileIdsToDelete, setFileIdsToDelete] = useState([]);

  const handleDelete = async (rowId, data) => {
    const removedArray = data.filter((row) => row.id !== rowId);
    const response = await updateProductFormData({
      categories,
      allItems: removedArray,
      productId: productDetails.productId,
      description,
      notes,
      files: files.filter((f) => !f.id), // only send new files
      fileIdsToDelete,
      agoraCategoryId: categories[0].agoraCategoryId,
      agoraSubcategoryId: categories[0].agoraSubcategoryId,
    });

    if (response.status === 200) {
      if (row !== undefined) {
        dispatch(getProductDetails(row.productId, false));
        dispatch(updateOrderItemFullInRedux(id, row.id));
      } else dispatch(getProductDetails(id, false));
      enqueueSnackbar('Item deleted successfully', { variant: 'success' });
    }
  };
  const [data, setData] = useState([]);
  const [editParent, setEditParent] = useState(false);
  const [categories, setCategories] = useState([
    {
      agoraCategoryId: null,
      agoraSubcategoryId: null,
    },
  ]);
  const [splitItems, setSplitItems] = useState(false);
  const [selected, setSelected] = useState([]);
  const { orderById } = useSelector((state) => state.orders || {});
  const { unsavedChanges } = orderById[id] || {};

  const {
    data: { productDetails, agoraCategories, agoraSubcategories },
  } = useSelector((state) => state.catalog);

  const isLoading = useSelector((state) => state.catalog.isLoading);

  useEffect(() => {
    if (productDetails.productDetails?.length > 0) {
      setData(productDetails.productDetails);

      setCategories([
        ...[
          {
            agoraCategoryId: productDetails?.agoraCategoryId,
            agoraSubcategoryId: productDetails?.agoraSubcategoryId,
          },
        ],
        ...productDetails.productCategories?.map((prodCat) => ({
          agoraCategoryId: prodCat.agoraCategoryId,
          agoraSubcategoryId: prodCat.agoraSubcategoryId,
        })),
      ]);

      setDescription(productDetails?.description);
      setNotes(productDetails?.notes);
    }
  }, [productDetails]);

  const [description, setDescription] = useState(productDetails?.description || '');
  const [notes, setNotes] = useState(productDetails?.notes || '');
  const [files, setFiles] = useState(productDetails?.files || []);
  const hasDescription = Boolean(description?.trim());

  useEffect(() => {
    setFiles(productDetails?.files || []);
  }, [productDetails.files]);

  const ActionButtons = (params) => (
    <>
      <ScopeGuard scopes={['Item-Catalog-Edit']} allowAdmin>
        <ScopeGuard scopes={['Edit-OrderGuide']} allowAdmin>
          <OrderGuideDialog productId={productDetails.productId} agoraCategoryId={productDetails.agoraCategoryId} deactivated={productDetails.deactivated} />
        </ScopeGuard>
        <EditItemDialog
          pageId={id}
          data={data}
          id={params.row.id}
          vendorItemId={params.row.vendorItemId}
          productDetails={productDetails}
          productId={productDetails.productId}
          description={productDetails.description}
          agoraCategoryId={productDetails.agoraCategoryId}
          agoraSubcategoryId={productDetails.agoraSubcategoryId}
          notes={productDetails.notes}
          files={productDetails.files}
          categories={categories}
          editParent={editParent}
          row={row}
        />
        <ConfirmDialog
          icon="delete-outline"
          title="Delete"
          color="error"
          content={`Are you sure you want to delete this item?`}
          actionButton="Delete"
          action={() => handleDelete(params.row.id, data)}
          disabled={editParent || unsavedChanges || row?.vendorItemId === params.row?.vendorItemId}
        />
      </ScopeGuard>
    </>
  );

  const actions = {
    field: 'actions',
    headerName: 'Actions',
    disableReorder: true,
    minWidth: 150,
    renderCell: (params) => ActionButtons(params),
    id: 'actions',
    className: 'actions',
  };

  const saveProduct = async () => {
    const response = await updateProductFormData({
      categories,
      allItems: data,
      productId: productDetails?.productId,
      description,
      notes,
      files,
      fileIdsToDelete,
      agoraCategoryId: categories[0].agoraCategoryId,
      agoraSubcategoryId: categories[0].agoraSubcategoryId,
    });
    if (response.status === 200) {
      if (row !== undefined) {
        dispatch(getProductDetails(row.productId, false));
        dispatch(updateOrderItemFullInRedux(id, row.id));
      } else dispatch(getProductDetails(id, false));
      enqueueSnackbar('Item updated successfully', { variant: 'success' });
      setEditParent(false);
    }
  };

  const splitData = async () => {
    const response = await splitProductData({
      productId: productDetails?.productId,
      itemsToSplit: selected,
    });
    if (response.status === 200) {
      dispatch(getProductDetails(id, false));
      enqueueSnackbar('Item split successfully', { variant: 'success' });
      setSelected([]);
      setSplitItems(false);
    }
  };

  const customToolbar = useCallback(
    () => (
      <Grid container direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          {splitItems && (
            <>
              <AwaitButton
                variant="contained"
                sx={{ ml: 1, mr: 1 }}
                size="small"
                onClick={splitData}
                disabled={selected.length < 1 || selected.length === data.length || productDetails?.editLocked}
              >
                Confirm Split
              </AwaitButton>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => {
                  setSelected([]);
                  setSplitItems(false);
                }}
              >
                Cancel
              </Button>{' '}
            </>
          )}
          {!splitItems && (
            <Tooltip
              title={'You do not have permission to edit catalog items'}
              disableHoverListener={!productDetails?.editLocked}
              arrow
              placement="top"
              slotProps={{
                tooltip: {
                  sx: { maxWidth: 180 },
                },
              }}
            >
              <Button
                variant="outlined"
                sx={{ ml: 1 }}
                size="small"
                onClick={() => setSplitItems(true)}
                startIcon={<Iconify icon={'lucide:split'} />}
                disabled={productDetails?.editLocked}
              >
                Split items
              </Button>
            </Tooltip>
          )}
        </Box>
      </Grid>
    ),
    [splitItems, selected.length, data.length, productDetails]
  );

  const gridColumns = [...CATALOG_ITEM, actions];
  const [openLinkDialog, setOpenLinkDialog] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  const insertHyperlink = () => {
    if (linkText && linkUrl) {
      setNotes((prev) => `${prev ?? ''}[${linkText}](${linkUrl})`);
    }
    setOpenLinkDialog(false);
    setLinkText('');
    setLinkUrl('');
  };

  const handleViewFile = (file) => {
    openProductFile(file.id)
      .then((res) => {
        const mimeType = res.headers['content-type'] || 'application/octet-stream';
        const blob = new Blob([res.data], { type: mimeType });
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
      })
      .catch(() => {
        enqueueSnackbar(`Error opening file: ${file.fileName}`, { variant: 'error' });
      });
  };

  return (
    <>
      <Grid item xs={12}>
        {!row && (
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item>
              <Button
                variant="text"
                onClick={() => {
                  const viewAllSkus = new URLSearchParams(window.location.search).get('viewallskus');
                  if (viewAllSkus) navigate(`/dashboard/admin/catalog?viewallskus=1`);
                  else navigate(`/dashboard/admin/catalog`);
                }}
                startIcon={<Iconify icon="mingcute:left-fill" width={24} height={24} sx={{ mx: -1 }} />}
                sx={{
                  color: '#454F5B',
                  fontWeight: 'bold',
                }}
              >
                Back to catalog
              </Button>
            </Grid>
          </Grid>
        )}
        <Grid container direction="row" justifyContent="flex-start" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Item info #{productDetails?.productId}
          </Typography>
          {productDetails?.deactivated && (
            <Chip size="small" color="warning" label="Deactivated" sx={{ ml: 1.5 }} />
          )}
          {!productDetails?.editLocked && (
            <Tooltip title={'Edit details'}>
              <IconButton
                color="secondary"
                disabled={unsavedChanges}
                onClick={(e) => {
                  e.edit = true;
                  setEditParent(true);
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={7}>
            <>
              <Grid container direction="row" justifyContent="space-between" alignItems="center">
                <Grid item xs={12}>
                  <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Description *</DialogContentText>{' '}
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      size="small"
                      disabled={!editParent}
                      label=""
                      fullWidth
                      multiline
                      value={description || ''}
                      variant="outlined"
                      onChange={(e) => {
                        setDescription(e.target.value);
                      }}
                      autoComplete="off"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          pr: editParent && hasDescription ? 6 : 1,
                        },
                      }}
                    />
                    {editParent && hasDescription && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          right: 8,
                          transform: 'translateY(-50%)',
                          zIndex: 1,
                        }}
                      >
                        <ItemAISuggestion
                          description={description || ''}
                          onSuggestionSelected={(suggestedDescription) => {
                            setDescription(suggestedDescription);
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {categories?.map((category, index) => (
                <Grid
                  key={`${index}-${category.agoraCategoryId}-${category.agoraSubcategoryId}`}
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-end"
                >
                  <Grid item xs={6}>
                    <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>
                      {index === 0 ? 'Main category * ' : index === 1 ? 'Additional categories *' : ' '}
                    </DialogContentText>
                    <Autocomplete
                      size="small"
                      disabled={!editParent}
                      sx={{
                        '& legend': { display: 'none' },
                        '& fieldset': { top: 0 },
                        '& .MuiFormLabelRoot ': 'none',
                        width: '90%',
                      }}
                      type="new-password"
                      options={agoraCategories || []}
                      autoHighlight
                      getOptionLabel={(option) => option?.name}
                      value={agoraCategories?.find((cat) => cat.id === categories[index].agoraCategoryId)}
                      onChange={(event, newValue) => {
                        setCategories((prevCategories) => {
                          const newCategories = [...prevCategories];
                          newCategories[index].agoraCategoryId = newValue?.id;
                          newCategories[index].agoraSubcategoryId = 0;
                          return newCategories;
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label=""
                          variant="outlined"
                          inputProps={{
                            ...params.inputProps,
                          }}
                          autoComplete="off"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={editParent ? 5 : 6}>
                    <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>
                      {index === 0 ? 'Main subcategory *' : index === 1 ? 'Additional subcategories *' : ' '}
                    </DialogContentText>
                    <Autocomplete
                      size="small"
                      disabled={!categories[index].agoraCategoryId || !editParent}
                      sx={{
                        '& legend': { display: 'none' },
                        '& fieldset': { top: 0 },
                        '& .MuiFormLabelRoot ': 'none',
                        width: editParent ? '90%' : '100%',
                      }}
                      type="new-password"
                      options={
                        agoraSubcategories.filter((cat) => cat.mainCategoryId === categories[index].agoraCategoryId) ||
                        []
                      }
                      autoHighlight
                      getOptionLabel={(option) => option?.name}
                      value={agoraSubcategories.find((cat) => cat.id === categories[index].agoraSubcategoryId)}
                      onChange={(event, newValue) => {
                        setCategories((prevCategories) => {
                          const newCategories = [...prevCategories];
                          newCategories[index].agoraSubcategoryId = newValue?.id;
                          return newCategories;
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label=""
                          variant="outlined"
                          inputProps={{
                            ...params.inputProps,
                          }}
                          autoComplete="off"
                        />
                      )}
                    />
                  </Grid>
                  {editParent && (
                    <Grid
                      item
                      xs={1}
                      sx={{
                        mt: 4,
                      }}
                    >
                      <IconButton
                        onClick={() => {
                          if (categories.length === 1) {
                            setCategories([{ agoraCategoryId: null, agoraSubcategoryId: null }]);
                          } else {
                            setCategories((prevCategories) => {
                              const newCategories = [...prevCategories];
                              newCategories?.splice(index, 1);
                              return newCategories;
                            });
                          }
                        }}
                      >
                        <Iconify icon="material-symbols:delete-outline" width={20} height={20} color="error" />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              ))}
              {editParent && (
                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                  <Button
                    variant="text"
                    color="primary"
                    sx={{
                      mt: 2,
                      textDecoration: 'underline',
                    }}
                    onClick={() => setCategories([...categories, { agoraCategoryId: null, agoraSubcategoryId: null }])}
                  >
                    Add additional category
                  </Button>
                </Grid>
              )}
            </>
          </Grid>

          <Grid item xs={5}>
            <DialogContentText sx={{ display: 'flex' }}>
              <Box sx={{ fontWeight: 'bold', mt: 2 }}>Notes</Box>
              {productDetails?.notesModifiedBy && productDetails?.notesDateModified && (
                <Box sx={{ mt: 2, mr: 0, ml: 'auto', fontSize: '12px' }}>
                  Last updated by {productDetails.notesModifiedBy} on{' '}
                  {new Date(productDetails?.notesDateModified).toLocaleDateString('en-US', {
                    year: '2-digit',
                    month: 'numeric',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                  })}
                </Box>
              )}
            </DialogContentText>
            {editParent ? (
              <>
                <Tooltip title={'Embed link'}>
                  <IconButton onClick={() => setOpenLinkDialog(true)}>
                    <Iconify icon="material-symbols:link" width={24} height={24} color="primary" />
                  </IconButton>
                </Tooltip>

                <Dialog
                  open={openLinkDialog}
                  onClose={(event, reason) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                    setOpenLinkDialog(false);
                  }}
                >
                  <DialogTitle>Embed Link</DialogTitle>
                  <DialogContent>
                    <TextField
                      label="Display Text"
                      fullWidth
                      margin="dense"
                      value={linkText}
                      onChange={(e) => setLinkText(e.target.value)}
                      autoComplete="off"
                    />
                    <TextField
                      label="URL"
                      fullWidth
                      margin="dense"
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                      autoComplete="off"
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setOpenLinkDialog(false)}>Cancel</Button>
                    <Button onClick={insertHyperlink} disabled={!linkText || !linkUrl}>
                      Insert
                    </Button>
                  </DialogActions>
                </Dialog>
                <ProductFileAttachmentButton setFiles={setFiles} />
                <TextField
                  disabled={!editParent}
                  size="small"
                  label=""
                  value={notes ?? ''}
                  onChange={(e) => {
                    setNotes(e.target.value);
                  }}
                  fullWidth
                  multiline
                  minRows={categories.length === 1 ? 5 : categories.length * 4}
                  variant="outlined"
                  autoComplete="off"
                />
                <Box sx={{ mt: 0.3, display: 'flex', flexWrap: 'wrap' }}>
                  {files.map((file) => (
                    // file will be an object with an id and a filename if it is already saved in the database
                    // if the file a new file and not yet saved in the backend, it will be a File object
                    <FileAttachment
                      key={file.id ? file.id : file.name}
                      file={file.id ? { name: file.fileName, path: file.fileName } : file}
                      fileName={file.id ? file.fileName : file.name}
                      canDeleteFile
                      handleClickAttachment={() =>
                        file.id ? handleViewFile(file) : window.open(URL.createObjectURL(file), '_blank')
                      }
                      handleDeleteFile={() => {
                        if (file.id) setFileIdsToDelete((prev) => [...prev, file.id]);
                        setFiles((prevFiles) =>
                          prevFiles.filter((f) => (f.id ? f.id !== file.id : f.name !== file.name))
                        );
                      }}
                    />
                  ))}
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ border: '1px solid #DEDEDE', borderRadius: 1, p: 1, pl: 1.4 }}>
                  <Typography sx={{ whiteSpace: 'pre-wrap', minHeight: '112px' }}>
                    {addHyperlink(notes ?? '')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 0.3 }}>
                  {files.map((file) => (
                    <FileAttachment
                      key={file.id}
                      file={{ name: file.fileName, path: file.fileName }}
                      fileName={file.fileName}
                      canDeleteFile={false}
                      handleClickAttachment={() => handleViewFile(file)}
                    />
                  ))}
                </Box>
              </>
            )}
          </Grid>
        </Grid>
        {editParent ? (
          <Grid container direction="row" justifyContent="flex-end" alignItems="center">
            {' '}
            <Button
              variant="outlined"
              color="error"
              size="medium"
              sx={{ width: 150, mr: 2, mt: 2 }}
              onClick={() => {
                setCategories([
                  {
                    agoraCategoryId: productDetails?.agoraCategoryId,
                    agoraSubcategoryId: productDetails?.agoraSubcategoryId,
                  },
                ]);
                setDescription(productDetails?.description);
                setNotes(productDetails?.notes);
                setEditParent(false);
                setFiles(productDetails?.files || []);
                setFileIdsToDelete([]);
              }}
            >
              Cancel
            </Button>
            <AwaitButton
              variant="contained"
              color="primary"
              size="medium"
              sx={{ width: 150, mt: 2 }}
              onClick={saveProduct}
              disabled={
                !description || categories.some((category) => !category.agoraCategoryId || !category.agoraSubcategoryId)
              }
            >
              Save
            </AwaitButton>{' '}
          </Grid>
        ) : (
          <ScopeGuard scopes={['Item-Catalog-Edit']} allowAdmin>
            <NewItemDialog
              parentInfo={{
                description,
                notes,
                categories,
                files: productDetails?.files,
                id: productDetails?.productId,
              }}
              row={row}
              data={data}
            />
          </ScopeGuard>
        )}
      </Grid>
      <Grid item xs={12}>
        <CustomDataGrid
          gridId="admin-catalog-item"
          boxSX={{ height: 'calc(100vh - 225px)' }}
          data={data}
          gridColumns={gridColumns}
          forceReRender={data}
          CustomLeftToolbar={customToolbar}
          sort={[{ field: 'perItem', sort: 'asc' }]}
          getRowId={(row) => row.id}
          checkboxSelection={splitItems}
          onRowSelectionModelChange={(newSelection) => {
            setSelected(newSelection);
          }}
          rowSelectionModel={selected}
          isLoading={isLoading}
        />
      </Grid>
    </>
  );
}
